import { FC } from 'react';
import Input from '@components/common/input';
import styles from './styles.module.scss';
import { useLocales } from '@hooks/helpers/useLocales';
import { passwordValidation } from '@utils/validation';
import Button from '@components/common/button';
import { useSettingsModals } from '@hooks/modals/useSettingsModals';
import { useForm } from '@hooks/helpers/useForm';
import { changePasswordValidation } from '@helpers/validation_objects/change_password';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import FormField from '@components/form_field';

interface IProps {
    onSuccess: () => void;
}

const PasswordSection: FC<IProps> = ({ onSuccess }) => {
    const { validationLocale, placeholderLocale, labelLocale, submitLocale } = useLocales();
    const { handleChangePassword } = useSettingsModals();
    const form = useForm({
        defaultValues: {
            oldPassword: '',
            password_confirm: '',
            newPassword: ''
        },
        resolver: zodResolver(
            changePasswordValidation({
                messages: {
                    oldPassword: validationLocale('password'),
                    newPassword: validationLocale('password'),
                    password_confirm: validationLocale('password_confirm')
                }
            })
        )
    });

    const onSubmit = () => {
        const values = form.getValues();
        form.trigger().then((isValid) => {
            isValid && handleChangePassword(values.oldPassword, values.newPassword, onSuccess);
        });
    };

    return (
        <div className={styles.wrapper}>
            <p>
                We do we can to keep your account secure, and we encourage you to do the same by
                updating your password every 3 months
            </p>
            <FormProvider {...form}>
                <div className={styles.form}>
                    <FormField
                        name="oldPassword"
                        renderComponent={(props) => {
                            return (
                                <Input
                                    full
                                    type="password"
                                    label={labelLocale('current_password')}
                                    placeholder={placeholderLocale('enter_current_password')}
                                    {...props}
                                />
                            );
                        }}
                    />
                    <FormField
                        name="newPassword"
                        renderComponent={({ value, ...props }) => {
                            return (
                                <Input
                                    full
                                    type="password"
                                    label={labelLocale('new_password')}
                                    placeholder={placeholderLocale('enter_new_password')}
                                    passwordErrors={passwordValidation(value, validationLocale)}
                                    value={value}
                                    {...props}
                                />
                            );
                        }}
                    />
                    <FormField
                        name="password_confirm"
                        renderComponent={(props) => {
                            return (
                                <Input
                                    full
                                    type="password"
                                    label={labelLocale('confirm_new_password')}
                                    placeholder={placeholderLocale('confirm_new_password')}
                                    {...props}
                                    disablePaste
                                />
                            );
                        }}
                    />
                    <Button variant="primary" onClick={onSubmit}>
                        {submitLocale('save_changes')}
                    </Button>
                </div>
            </FormProvider>
        </div>
    );
};

export default PasswordSection;
